/* eslint-disable quotes */
"use client"
import { keyframes } from "@emotion/react"
import styled from "@emotion/styled"
import { IconButton } from "@mui/material"
import { useLocale, useTranslations } from "next-intl"
import { useCallback, useEffect, useRef, useState } from "react"
import { NewText } from "ui"
import VideoPauseIcon from "ui/icons/video/VideoPauseIcon"
import VideoPlayIcon from "ui/icons/video/VideoPlayIcon"
import { sharedColors as colors } from "ui/theme/colors"

const formatNumber = (number: number) => {
  return new Intl.NumberFormat("en-US").format(number)
}

const marqueeAnimation = keyframes`
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
`

const StyledBanner = styled.div`
  height: 38px;
  border-top: 1px solid #6f6f6f;
  background-color: white;
  position: fixed;
  top: 64px;
  left: 0;
  width: 100%;
  z-index: 10;
  box-shadow: 0px 4px 16px 0px rgba(0, 0, 0, 0.16);
  overflow: hidden;
  &::before {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    height: 100%;
    width: 62px;
    background: white;
    z-index: 2;
    pointer-events: none;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    top: 98px;
    &::before {
      width: calc(56px + max(16px, (100% - 1040px) / 2 - 20px));
    }
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    &::before {
      width: calc(56px + max(16px, (100% - 1280px) / 2 - 10px));
    }
  }
`

const MarqueeTrack = styled.div<{ isPaused: boolean; duration: number }>`
  display: flex;
  align-items: center;
  height: 100%;
  width: fit-content;
  animation: ${marqueeAnimation} ${(props) => props.duration}s linear infinite;
  animation-play-state: ${(props) => (props.isPaused ? "paused" : "running")};
`

const MarqueeContent = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`

const StyledSlide = styled.div`
  display: flex;
  align-items: center;
  padding: 0 8px;
`

const StyledLine = styled.div`
  height: 26px;
  width: 1px;
  background-color: #6f6f6f;
  margin-left: 16px;
  margin-right: 16px;
`

const StyledSlider = styled.div`
  position: relative;
  z-index: 1;
`

type StockData = {
  basePrice: number
  changeValue: number
  lastTransaction: string
  securityDailyAggVolume: number
  securityLastRate: number
  securityPercentageChange: number
  symbol: string
}

type Props = {
  stockBanner: StockData
  change: string
  lasttransaction: string
  StockRate: string
  cents: string
  baseRate: string
}

function StockBanner({ stockBanner, change, StockRate, cents, baseRate }: Props) {
  const [isPlaying, setIsPlaying] = useState(true)
  const [duration, setDuration] = useState(30)
  const contentRef = useRef<HTMLDivElement>(null)
  const locale = useLocale()
  const t = useTranslations("StockBanner")

  // Calculate animation duration based on content width
  useEffect(() => {
    if (contentRef.current) {
      const contentWidth = contentRef.current.offsetWidth
      // Aim for ~50px per second scrolling speed
      const calculatedDuration = Math.max(contentWidth / 50, 20)
      setDuration(calculatedDuration)
    }
  }, [stockBanner])

  const handleToggleAutoplay = useCallback(() => {
    setIsPlaying((prev) => !prev)
  }, [])

  if (!stockBanner) return null

  const renderContent = () => (
    <>
      <StyledSlide>
        <NewText
          component={"p"}
          desktopFontSize={18}
          desktopLineHeight={"26px"}
          mobileFontSize={16}
          mobileLineHeight={"23px"}
          desktopFontWeight={400}
          color={colors.black}
          marginRight={"8px"}
        >
          {`${StockRate}: ${stockBanner.symbol}`}
        </NewText>
        <NewText
          component={"p"}
          desktopFontSize={18}
          desktopLineHeight={"26px"}
          mobileFontSize={16}
          mobileLineHeight={"23px"}
          desktopFontWeight={400}
          color={colors.black}
        >
          {`${formatNumber(stockBanner.securityLastRate)} ${cents}`}
        </NewText>
        <StyledLine />
      </StyledSlide>
      <StyledSlide>
        <NewText
          component={"p"}
          desktopFontSize={18}
          desktopLineHeight={"26px"}
          mobileFontSize={16}
          mobileLineHeight={"23px"}
          desktopFontWeight={400}
          color={colors.black}
          marginRight={"8px"}
        >
          {change}:
        </NewText>
        <NewText
          component={"p"}
          desktopFontSize={18}
          desktopLineHeight={"26px"}
          mobileFontSize={16}
          mobileLineHeight={"23px"}
          desktopFontWeight={400}
          color={stockBanner.securityPercentageChange > 0 ? "#008015" : "#EE0000"}
          style={{ unicodeBidi: "plaintext" }}
          marginRight={"8px"}
        >
          {stockBanner.changeValue}
        </NewText>
        <NewText
          component={"p"}
          desktopFontSize={18}
          desktopLineHeight={"26px"}
          mobileFontSize={16}
          mobileLineHeight={"23px"}
          desktopFontWeight={400}
          color={stockBanner.securityPercentageChange > 0 ? "#008015" : "#EE0000"}
          style={{ unicodeBidi: "plaintext" }}
          marginRight={"8px"}
        >
          {stockBanner.securityPercentageChange}%
        </NewText>
        <NewText
          component={"p"}
          desktopFontSize={18}
          desktopLineHeight={"26px"}
          mobileFontSize={16}
          mobileLineHeight={"23px"}
          desktopFontWeight={400}
          color={colors.black}
          marginRight={"8px"}
        >
          /
        </NewText>
        <NewText
          component={"p"}
          desktopFontSize={18}
          desktopLineHeight={"26px"}
          mobileFontSize={16}
          mobileLineHeight={"23px"}
          desktopFontWeight={400}
          color={colors.black}
          marginRight={"8px"}
        >
          {locale === "he" ? 'מחזור ע"נ' : "NV turnover"}
        </NewText>
        <NewText
          component={"p"}
          desktopFontSize={18}
          desktopLineHeight={"26px"}
          mobileFontSize={16}
          mobileLineHeight={"23px"}
          desktopFontWeight={400}
          color={colors.black}
        >
          {formatNumber(stockBanner.securityDailyAggVolume)}
        </NewText>
        <StyledLine />
      </StyledSlide>
      <StyledSlide>
        <NewText
          component={"p"}
          desktopFontSize={18}
          desktopLineHeight={"26px"}
          mobileFontSize={16}
          mobileLineHeight={"23px"}
          desktopFontWeight={400}
          color={colors.black}
        >
          {baseRate}: {formatNumber(stockBanner.basePrice)}
        </NewText>
        <StyledLine />
      </StyledSlide>
    </>
  )

  return (
    <StyledSlider>
      <StyledBanner>
        <MarqueeTrack isPaused={!isPlaying} duration={duration}>
          {/* First copy - used for measurement and display */}
          <MarqueeContent ref={contentRef}>
            {renderContent()}
            {renderContent()}
            {renderContent()}
            {renderContent()}
          </MarqueeContent>
          {/* Second copy - creates seamless loop */}
          <MarqueeContent>
            {renderContent()}
            {renderContent()}
            {renderContent()}
            {renderContent()}
          </MarqueeContent>
        </MarqueeTrack>
      </StyledBanner>
      <IconButton
        aria-label={isPlaying ? t("stopMarquee") : t("playMarquee")}
        aria-pressed={isPlaying}
        onClick={handleToggleAutoplay}
        disableRipple
        sx={{
          overflow: "hidden",
          position: "fixed",
          zIndex: 12,
          top: {
            xs: "67px",
            md: "101px",
          },
          padding: 0,
          width: "48px",
          height: "48px",
          right: {
            xs: "6px",
            md: "max(16px, calc((100% - 1040px) / 2 - 20px))",
            lg: "max(16px, calc((100% - 1280px) / 2 - 10px))",
          },
        }}
      >
        {isPlaying ? <VideoPauseIcon /> : <VideoPlayIcon />}
      </IconButton>
    </StyledSlider>
  )
}

export default StockBanner
